/**
 * splitLessonSections — Phase 107 Lesson Workspace section router.
 *
 * Walks the lesson_markdown (or unit_markdown / summative_markdown)
 * heading by heading and partitions every H2 section into one of:
 *
 *   • overview  — planning view: WALT, Success Criteria, curriculum,
 *                  transferable skill focus, Learning Skill, Career
 *                  Connection, Materials, Vocabulary, Misconceptions,
 *                  Teacher Moves, Look-fors, etc.
 *   • students  — worksheets, handouts, exit tickets, student reading
 *   • reporting — the Report Card Language section
 *
 * Plus a top-level title (an H1) is treated as "title" and removed
 * from the body output (the workspace renders the activity_name in
 * the page header).
 *
 * Heuristics intentionally permissive — the LLM occasionally varies
 * heading phrasing. Anything we can't classify lands in overview so
 * the teacher never loses content.
 */

const STUDENT_HEADING_PATTERNS = [
  /^worksheet/i,
  /^student\s+worksheet/i,
  /^student\s+handout/i,
  /^student\s+sheet/i,
  /^student[-\s]facing/i,
  /^student\s+planner/i,
  /^student\s+log/i,
  /^student\s+graphic\s+organizer/i,
  /^graphic\s+organizer/i,
  /^exit\s+ticket/i,
  /^exit[-\s]slip/i,
  /^reading\s+handout/i,
  /^answer\s+key/i,
  /^student\s+diagnostic/i,
  /^research\s+sheet/i,
  /^reference\s+card/i,
];

const REPORTING_HEADING_PATTERNS = [
  /^report\s*card\s*language/i,
  /^reporting\s*language/i,
  /^reporting$/i,
];

const SCRIPT_HEADING_PATTERNS = [
  /^teacher\s+script/i,
  /^running\s+the\s+lesson/i,
  /^minute[-\s]by[-\s]minute/i,
  /^beat[-\s]by[-\s]beat/i,
];

function classify(heading) {
  const h = (heading || "").trim().replace(/^#+\s*/, "");
  if (REPORTING_HEADING_PATTERNS.some((re) => re.test(h))) return "reporting";
  if (STUDENT_HEADING_PATTERNS.some((re) => re.test(h))) return "students";
  if (SCRIPT_HEADING_PATTERNS.some((re) => re.test(h))) return "scriptish";
  return "overview";
}

export function splitLessonSections(markdown) {
  const result = {
    overview: "",
    students: [],
    reporting: "",
  };
  if (!markdown || typeof markdown !== "string") return result;

  const lines = markdown.split("\n");
  let bucket = "overview";
  let currentText = "";
  let currentHeadingLevel = 0;

  const flush = () => {
    const text = currentText.trim();
    if (!text) return;
    if (bucket === "students") {
      result.students.push(text);
    } else if (bucket === "reporting") {
      // Concatenate (rare to have multiple reporting sections, but
      // tolerate the LLM splitting it across two headings).
      result.reporting = result.reporting
        ? `${result.reporting}\n\n${text}`
        : text;
    } else if (bucket === "overview") {
      result.overview = result.overview ? `${result.overview}\n\n${text}` : text;
    }
    // "scriptish" content is dropped from overview — the Teacher Script
    // tab renders activity.teacher_script_markdown directly.
    currentText = "";
  };

  for (const line of lines) {
    const h1 = line.match(/^#\s+(.+?)\s*$/);
    const h2 = line.match(/^##\s+(.+?)\s*$/);
    const h3 = line.match(/^###\s+(.+?)\s*$/);
    if (h1 && currentHeadingLevel === 0) {
      // Strip the very first H1 (lesson title) — page header renders it.
      currentHeadingLevel = 1;
      continue;
    }
    if (h2) {
      flush();
      bucket = classify(h2[1]);
      currentText = `${line}\n`;
      currentHeadingLevel = 2;
      continue;
    }
    if (h3 && currentHeadingLevel === 1) {
      // Some lessons jump straight to H3 with no H2 in between — treat
      // them like H2 for routing purposes.
      flush();
      bucket = classify(h3[1]);
      currentText = `${line}\n`;
      currentHeadingLevel = 3;
      continue;
    }
    currentText += `${line}\n`;
    if (currentHeadingLevel === 0 && line.trim()) {
      currentHeadingLevel = 1;
    }
  }
  flush();
  return result;
}

export default splitLessonSections;
